import { useMemo, useRef, useEffect } from 'react'
import { ErrorBoundary } from '@sentry/react'
import dayjs from 'dayjs'

import type { View as ViewModel, Image as ImageModel } from '@/lib/api/client'
import type { Step } from '@/services/queries/operation_steps/types'

import { Image } from '@/components/pdf/Image'
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Spinner } from '@/components/ui/spinner'

import { useStepOrderNumber } from '../AssemblyOrderPanel/hooks/useStepOrderNumber'
import { useCADPageStore } from '@/state'
import { useShallow } from 'zustand/react/shallow'

export const Screenshot = ({
  index,
  view,
  steps,
  isReadOnly,
  isDisabled,
  isLoading,
  onRetake,
  onDelete,
  onClick,
  isFocused,
}: {
  index: number
  view: ViewModel | ImageModel
  steps?: Step[]
  isReadOnly: boolean
  isDisabled?: boolean
  isLoading?: boolean
  isFocused?: boolean
  onRetake?: () => void
  onDelete?: () => void
  onClick: (attrs: {
    attachedStep?: Step
    event: React.MouseEvent<HTMLImageElement, MouseEvent>
  }) => void
}) => {
  const downloadUrl = view.download_url

  const containerRef = useRef<HTMLDivElement>(null)
  const setCadPageState = useCADPageStore(useShallow((state) => state.setState))

  const attachedStep = useMemo(
    () => steps?.find((step) => step.id === (view as ViewModel).document_page),
    [steps, view],
  )

  const {
    data: { orderNumber },
  } = useStepOrderNumber({ step: attachedStep })

  useEffect(() => {
    if (containerRef.current && isFocused) {
      setCadPageState({ retakeViewId: null })
      containerRef.current.focus()
    }
  }, [isFocused, setCadPageState])

  if (!downloadUrl) {
    return null
  }

  return (
    <div
      ref={containerRef}
      tabIndex={index}
      id={`view-${view.id}-screenshot-container`}
      data-testid={`view-${view.id}-screenshot-container`}
      className="max-w-lg flex flex-col border rounded-xl p-2 relative w-[230px] h-[250px] focus:outline-none focus:border-2 focus:border-primary-50"
    >
      <ErrorBoundary fallback={<div>Failed to load image</div>}>
        <div className="flex h-full flex-col">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              {' '}
              {attachedStep && orderNumber && (
                <div className="text-sm font-medium text-gray-900 truncate">
                  {attachedStep.name || `Operation ${orderNumber}`}
                </div>
              )}
              {(view as ViewModel).created && (
                <div className="text-xs text-gray-500 whitespace-nowrap">
                  {dayjs((view as ViewModel).created).format(
                    'MMM DD, YYYY hh:mm A',
                  )}
                </div>
              )}
            </div>

            {!isReadOnly && onDelete && (
              <DropdownMenu>
                <DropdownMenuTrigger
                  className="px-1 py-2 rounded-lg"
                  data-testid={`step-${attachedStep?.order_number}-screenshot-options-button`}
                >
                  <EllipsisVerticalIcon className="w-4 h-4 text-gray-500" />
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem
                    disabled={(view as ViewModel).dynamic ? false : true}
                    onMouseDown={onRetake}
                    data-testid={`step-${attachedStep?.order_number}-screenshot-retake-button`}
                  >
                    Re-take
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onMouseDown={onDelete}
                    data-testid={`step-${attachedStep?.order_number}-delete-screenshot-button`}
                  >
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          <div className="mt-2 relative flex-1">
            {(isDisabled || isLoading) && (
              <div className="absolute inset-0 flex items-center justify-center">
                {isLoading && <Spinner />}
              </div>
            )}

            <div className="h-full w-full flex items-center justify-center overflow-hidden rounded-lg">
              <Image
                src={downloadUrl}
                className="cursor-pointer max-h-full max-w-full object-contain"
                style={{
                  width: '100%',
                  height: '100%',
                }}
                onMouseDown={(event) => {
                  onClick({ attachedStep: attachedStep, event })
                }}
                data-testid={`step-${attachedStep?.order_number}-screenshot`}
              />
            </div>
          </div>
        </div>
      </ErrorBoundary>
    </div>
  )
}
